import { useState } from 'react'
import { api, formatDayMonthTime } from '../../utils/api'
import PauseIcon from './PauseIcon'
import { HOLD_REASONS, HOLD_NOTE_MAX_LENGTH, holdReasonLabel } from '../../utils/orderHoldReasons'

/**
 * OrderHoldControl: the hold banner and the "Put on hold" form for one order.
 * A held order is never picked into a printrun and cannot be shipped (spec
 * 20261027). Prism takes the staff name from the signed-in user, so no name is
 * sent from here.
 *
 *   hold       the order's open hold { reason, held_by, held_at }, or null
 *   onChange   (hold | null) => void, called after Prism accepted the change
 */

const BANNER_STYLE = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-2)',
  background: 'var(--color-warning-light, #fff4d1)',
  border: '1px solid var(--color-warning, #ffd44d)',
  borderRadius: 8,
  padding: '0.75rem 1rem',
  margin: '0.5rem 0 1rem',
  fontSize: '0.9em'
}

// The note reads as a sentence: give it a full stop when the writer left none.
function sentence(text) {
  return /[.!?]$/.test(text) ? text : `${text}.`
}
const ERROR_STYLE = {
  marginTop: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)',
  background: 'var(--color-error-bg, #fdecea)',
  color: 'var(--color-error, #b71c1c)',
  border: '1px solid var(--color-error, #b71c1c)',
  borderRadius: 6,
  fontSize: '0.8rem'
}

export default function OrderHoldControl({ marketplace, orderNumber, hold, onChange }) {
  const holdPath = `/api/orders/${marketplace}/${orderNumber}/hold`
  const [formOpen, setFormOpen] = useState(false)
  const [reason, setReason] = useState(HOLD_REASONS[0].value)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  async function run(request) {
    setBusy(true)
    setError(null)
    try {
      onChange(await request())
      setFormOpen(false)
      setNote('')
    } catch (err) {
      setError(err.message || 'Could not change the hold')
    } finally {
      setBusy(false)
    }
  }

  function submitHold(e) {
    e.preventDefault()
    const trimmed = note.trim()
    const body = trimmed ? { reason, note: trimmed } : { reason }
    run(() => api.post(holdPath, body))
  }

  function release() {
    const label = holdReasonLabel(hold.reason)
    if (!window.confirm(`Release the hold (${label})? The order can then be picked and shipped.`)) return
    run(async () => {
      await api.post(`${holdPath}/release`, {})
      return null
    })
  }

  const errorBox = error && <div role="alert" style={ERROR_STYLE}>{error}</div>

  if (hold) {
    return (
      <div>
        <div style={BANNER_STYLE}>
          <span style={{ paddingTop: 3 }}><PauseIcon size={14} /></span>
          <div>
            <div><strong>On hold · {holdReasonLabel(hold.reason)}</strong></div>
            <div>
              {hold.note ? `${sentence(hold.note)} ` : ''}
              Held by {hold.held_by}, {formatDayMonthTime(hold.held_at)}. Picking and shipping are blocked.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginLeft: 'auto', background: '#fff', flex: 'none', alignSelf: 'center' }}
            onClick={release}
            disabled={busy}
          >
            Release hold
          </button>
        </div>
        {errorBox}
      </div>
    )
  }

  if (!formOpen) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '0.5rem 0' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFormOpen(true)}>
          Put on hold
        </button>
      </div>
    )
  }

  return (
    <form
      onSubmit={submitHold}
      className="order-detail-section"
      style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, marginTop: '0.5rem' }}
    >
      <h3 style={{ marginTop: 0 }}>Put on hold</h3>
      <div style={{ marginBottom: 12 }}>
        <label htmlFor="hold-reason" className="filter-label">Reason</label>
        <select id="hold-reason" className="form-input" value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}>
          {HOLD_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label htmlFor="hold-note" className="filter-label">Note (optional)</label>
        <textarea
          id="hold-note"
          className="form-input"
          rows={2}
          maxLength={HOLD_NOTE_MAX_LENGTH}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          style={{ width: '100%', resize: 'vertical' }}
        />
      </div>
      {errorBox}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
        <button type="button" className="btn btn-secondary" onClick={() => setFormOpen(false)} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  )
}
